import { useState } from "react"

import { VoicePicker } from "@/components/agents/setup/receptionist/voice-picker"
import type { VoiceOption } from "@/components/agents/setup/types"
import InputError from "@/components/input-error"
import ImageUpload from "@/components/ui/image-upload"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/text-area"

const BILLING_TYPE_OPTIONS = [
  {
    value: "imperative_managed",
    label: "Imperative-managed",
    description:
      "Imperative bills end-customers via Stripe and pays out the partner share.",
  },
  {
    value: "partner_managed",
    label: "Partner-managed",
    description:
      "The partner handles end-customer billing on their own — no Stripe checkout in the activation flow.",
  },
] as const

type BillingType = (typeof BILLING_TYPE_OPTIONS)[number]["value"]

interface PartnerFormProps {
  data: {
    partner: {
      name: string
      description: string
      logo: File | null
      logo_wordmark: File | null
      demo_mode?: boolean
      scheduling_url?: string
      subdomain?: string
      billing_type?: BillingType
      default_voice_model?: string
    }
    remove_logo?: boolean
    remove_logo_wordmark?: boolean
  }
  setData: (key: string, value: any) => void
  errors: Record<string, any>
  logoUrl: string | null
  logoWordmarkUrl: string | null
  voices?: VoiceOption[]
}

export default function PartnerForm({
  data,
  setData,
  errors,
  logoUrl,
  logoWordmarkUrl,
  voices,
}: PartnerFormProps) {
  const [preview, setPreview] = useState<string | null>(null)
  const [wordmarkPreview, setWordmarkPreview] = useState<string | null>(null)
  const removed = Boolean(data.remove_logo)
  const wordmarkRemoved = Boolean(data.remove_logo_wordmark)

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files[0]) {
      setData("partner", { ...data.partner, logo: e.target.files[0] })
      setData("remove_logo", false)
      setPreview(URL.createObjectURL(e.target.files[0]))
    }
  }

  function handleRemoveLogo() {
    setData("partner", { ...data.partner, logo: null })
    setData("remove_logo", true)
    setPreview(null)
  }

  function handleWordmarkChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files[0]) {
      setData("partner", { ...data.partner, logo_wordmark: e.target.files[0] })
      setData("remove_logo_wordmark", false)
      setWordmarkPreview(URL.createObjectURL(e.target.files[0]))
    }
  }

  function handleRemoveWordmark() {
    setData("partner", { ...data.partner, logo_wordmark: null })
    setData("remove_logo_wordmark", true)
    setWordmarkPreview(null)
  }

  return (
    <>
      <div className="grid gap-2">
        <Label htmlFor="name">Name</Label>
        <Input
          id="name"
          type="text"
          className="mt-1 block w-full"
          defaultValue={data.partner.name}
          placeholder="Partner name"
          onChange={(e) =>
            setData("partner", { ...data.partner, name: e.target.value })
          }
        />
        <InputError message={(errors as any)?.name ?? undefined} />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          className="mt-1 block w-full"
          defaultValue={data.partner.description}
          placeholder="Brief description"
          onChange={(e) =>
            setData("partner", {
              ...data.partner,
              description: e.target.value,
            })
          }
        />
        <InputError message={(errors as any)?.description ?? undefined} />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="subdomain">Subdomain</Label>
        <Input
          id="subdomain"
          type="text"
          className="mt-1 block w-full"
          defaultValue={data.partner.subdomain ?? ""}
          placeholder="acme"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          onChange={(e) =>
            setData("partner", {
              ...data.partner,
              subdomain: e.target.value,
            })
          }
        />
        <p className="text-muted-foreground text-xs">
          Lowercase letters, numbers, and hyphens. Used as the partner&rsquo;s
          login entry point (e.g. <code>acme.imperative.app</code>).
        </p>
        <InputError message={(errors as any)?.subdomain ?? undefined} />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="scheduling_url">Onboarding scheduling URL</Label>
        <Input
          id="scheduling_url"
          type="url"
          className="mt-1 block w-full"
          defaultValue={data.partner.scheduling_url ?? ""}
          placeholder="https://cal.com/your-partner/onboarding"
          onChange={(e) =>
            setData("partner", {
              ...data.partner,
              scheduling_url: e.target.value,
            })
          }
        />
        <p className="text-muted-foreground text-xs">
          External booking link for the home-page &ldquo;Schedule onboarding
          call&rdquo; button. Leave blank to hide the CTA.
        </p>
        <InputError message={(errors as any)?.scheduling_url ?? undefined} />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="billing_type">Billing type</Label>
        <Select
          value={data.partner.billing_type ?? "partner_managed"}
          onValueChange={(val) =>
            setData("partner", {
              ...data.partner,
              billing_type: val as BillingType,
            })
          }
        >
          <SelectTrigger id="billing_type" className="w-full">
            <SelectValue placeholder="Choose a billing type" />
          </SelectTrigger>
          <SelectContent>
            {BILLING_TYPE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-muted-foreground text-xs">
          {
            BILLING_TYPE_OPTIONS.find(
              (o) =>
                o.value === (data.partner.billing_type ?? "partner_managed"),
            )?.description
          }
        </p>
        <InputError message={(errors as any)?.billing_type ?? undefined} />
      </div>

      {voices && voices.length > 0 && (
        <div className="grid gap-2">
          <div className="flex items-center justify-between">
            <Label>Default voice model</Label>
            {data.partner.default_voice_model ? (
              <button
                type="button"
                className="text-muted-foreground text-xs hover:underline"
                onClick={() =>
                  setData("partner", {
                    ...data.partner,
                    default_voice_model: "",
                  })
                }
              >
                Clear
              </button>
            ) : null}
          </div>
          <p className="text-muted-foreground text-xs">
            New receptionists for this partner start on this voice. Leave unset
            to use the global default.
          </p>
          <VoicePicker
            voices={voices}
            selected={data.partner.default_voice_model ?? ""}
            onSelect={(id) =>
              setData("partner", { ...data.partner, default_voice_model: id })
            }
          />
          <InputError
            message={(errors as any)?.default_voice_model ?? undefined}
          />
        </div>
      )}

      <ImageUpload
        label="Icon"
        id="logo"
        preview={preview ?? undefined}
        currentUrl={removed ? null : (logoUrl ?? undefined)}
        onFileChange={handleFileChange}
        onRemove={handleRemoveLogo}
        error={(errors as any)?.logo ?? undefined}
        buttonLabel="Upload icon"
      />

      <ImageUpload
        label="Logo"
        id="logo_wordmark"
        preview={wordmarkPreview ?? undefined}
        currentUrl={wordmarkRemoved ? null : (logoWordmarkUrl ?? undefined)}
        onFileChange={handleWordmarkChange}
        onRemove={handleRemoveWordmark}
        error={(errors as any)?.logo_wordmark ?? undefined}
        buttonLabel="Upload logo"
      />

      <div className="grid gap-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="demo_mode" className="cursor-pointer">
            Demo mode
          </Label>
          <Switch
            id="demo_mode"
            checked={data.partner.demo_mode ?? false}
            onCheckedChange={(checked) =>
              setData("partner", { ...data.partner, demo_mode: checked })
            }
          />
        </div>
        <p className="text-muted-foreground text-xs">
          Populate the partner portal with realistic placeholder data for
          demonstration purposes.
        </p>
      </div>
    </>
  )
}
